import { appendItemToArray, removeItemFromArray } from './arrays';
import { onNextFrame } from './timers';

export type EventEmitter<T> = {
  addEventListener: (eventName: string, listener: Listener<T>) => void;
  emit: (eventName: string, event: T) => void;
  removeEventListener: (eventName: string, listener: Listener<T>) => void;
};

export type Listener<T> = {
  (event: T): void;
};

export const buildEventEmitter = <T>(eventNames: string[]): EventEmitter<T> => {
  const listeners = new Map<string, Listener<T>[]>();
  const validEventNames = ['*', ...eventNames];

  const validateEventName = (eventName: string) => {
    if (!validEventNames.includes(eventName)) {
      throw new Error(`${JSON.stringify(eventName)} is not a valid event name`);
    }
  };

  const addEventListener = (eventName: string, listener: Listener<T>) => {
    validateEventName(eventName);

    listeners.set(
      eventName,
      appendItemToArray(listeners.get(eventName) || [], listener),
    );
  };

  const emit = (eventName: string, event: T) => {
    validateEventName(eventName);

    onNextFrame(() => {
      [
        ...(listeners.get('*') || []),
        ...(listeners.get(eventName) || []),
      ].forEach((listener) => listener(event));
    });
  };

  const removeEventListener = (eventName: string, listener: Listener<T>) => {
    validateEventName(eventName);

    listeners.set(
      eventName,
      removeItemFromArray(listeners.get(eventName) || [], listener),
    );
  };

  return {
    addEventListener,
    emit,
    removeEventListener,
  };
};
